import React from 'react';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header - Same as landing page */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-lg border-b border-slate-900/10">
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center space-x-2 text-2xl font-bold text-slate-800">
            <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span>ThreadIt</span>
          </a>

          <div className="hidden md:flex items-center space-x-8">
            <a href="/" className="text-slate-600 hover:text-emerald-500 transition-colors">Home</a>
            <a href="/pricing" className="text-slate-600 hover:text-emerald-500 transition-colors">Pricing</a>
            <a href="/contact" className="text-slate-600 hover:text-emerald-500 transition-colors">Contact</a>
          </div>

          <div className="hidden md:block">
            <a 
              href="/app" 
              className="inline-block text-center px-6 py-2 rounded-lg font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-100 bg-emerald-500 text-white hover:bg-emerald-600 focus:ring-emerald-500 shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40"
            >
              Login
            </a>
          </div>

          <div className="md:hidden">
            <button aria-label="Toggle menu" className="text-slate-800">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Privacy Policy for ThreadIt</h1>
          <p className="text-slate-600 mb-8">Last Updated: August 21, 2025</p>
          
          <div className="bg-white rounded-lg shadow-md p-8 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">1. Introduction</h2>
              <p className="text-slate-700 leading-relaxed">
                Welcome to ThreadIt ("we," "us," or "our"). We are committed to protecting your privacy and handling your data in an open and transparent manner. This privacy policy explains how we collect, use, and protect your personal information when you visit our website, www.threadit.ai (the "Service").
              </p>
            </section>

            {/* Data Controller */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">2. Data Controller</h2>
              <p className="text-slate-700 mb-2">The data controller responsible for your personal data is:</p>
              <div className="text-slate-700 space-y-1 ml-4">
                <p className="font-medium">Truscova GmbH</p>
                <p>Wilhelm-Liebknicht-Str. 4</p>
                <p>28329 Bremen</p>
                <p>Germany</p>
                <p>Email: hello@threadit.ai</p>
              </div>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">3. Information We Collect</h2>
              <p className="text-slate-700 mb-4">We collect information in the following ways:</p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-medium text-slate-800 mb-2">Information You Provide Directly:</h3>
                  <ul className="text-slate-700 space-y-2 ml-4">
                    <li><span className="font-medium">Email Subscriptions:</span> When you subscribe to our notification list, we collect your email address.</li>
                    <li><span className="font-medium">Tool Submissions:</span> When you submit a tool for review, we collect the information you provide in the form, which may include your name, contact email, and details about the tool.</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-medium text-slate-800 mb-2">Information We Collect Automatically:</h3>
                  <ul className="text-slate-700 space-y-2 ml-4">
                    <li><span className="font-medium">Usage Data:</span> We use standard analytics tools (like Google Analytics) to collect non-personally identifiable information about your interaction with our Service. This may include your IP address, browser type, pages visited, and the time and date of your visit.</li>
                    <li><span className="font-medium">Cookies:</span> We use cookies to help our site function and to understand user behavior. For more information, please see our Cookie Policy section.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">4. How We Use Your Information</h2>
              <p className="text-slate-700 mb-4">We use the information we collect for the following purposes:</p>
              <ul className="text-slate-700 space-y-2 ml-4">
                <li><span className="font-medium">To Provide and Maintain our Service:</span> To operate the website and display the leaderboard.</li>
                <li><span className="font-medium">To Communicate With You:</span> To send you notifications about the launch of our tools and other relevant project updates, but only if you have opted in.</li>
                <li><span className="font-medium">To Improve Our Service:</span> To analyze usage data to understand how our visitors use the site and to improve its functionality and user experience.</li>
                <li><span className="font-medium">To Process Submissions:</span> To review and manage the tools submitted to our platform.</li>
              </ul>
            </section>

            {/* Legal Basis for Processing */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">5. Legal Basis for Processing (GDPR)</h2>
              <p className="text-slate-700 mb-4">As we are based in Germany, our legal basis for processing personal data under the General Data Protection Regulation (GDPR) is as follows:</p>
              <ul className="text-slate-700 space-y-2 ml-4">
                <li><span className="font-medium">Consent:</span> We process your email address for our notification list based on your explicit consent when you sign up.</li>
                <li><span className="font-medium">Legitimate Interests:</span> We process usage data and tool submission data based on our legitimate interest in operating and improving our platform and its content.</li>
              </ul>
            </section>

            {/* Data Sharing and Third Parties */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">6. Data Sharing and Third Parties</h2>
              <p className="text-slate-700 mb-4">We do not sell your personal data. We only share your data with trusted third-party service providers who help us operate our Service:</p>
              <ul className="text-slate-700 space-y-2 ml-4">
                <li><span className="font-medium">Email Marketing:</span> We use Brevo to manage our email subscription list. Your email address is shared with them for the sole purpose of sending you the notifications you subscribed to.</li>
                <li><span className="font-medium">Analytics:</span> We use Google Analytics to analyze website traffic.</li>
                <li><span className="font-medium">Security:</span> We use Google reCAPTCHA to protect our forms from spam and abuse.</li>
              </ul>
            </section>

            {/* Your Data Protection Rights */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">7. Your Data Protection Rights (GDPR)</h2>
              <p className="text-slate-700 mb-4">You have the following rights regarding your personal data:</p>
              <ul className="text-slate-700 space-y-2 ml-4">
                <li><span className="font-medium">The right to access:</span> You can request copies of your personal data.</li>
                <li><span className="font-medium">The right to rectification:</span> You can request that we correct any information you believe is inaccurate.</li>
                <li><span className="font-medium">The right to erasure:</span> You can request that we erase your personal data, under certain conditions.</li>
                <li><span className="font-medium">The right to restrict processing:</span> You can request that we restrict the processing of your personal data, under certain conditions.</li>
                <li><span className="font-medium">The right to object to processing:</span> You can object to our processing of your personal data, under certain conditions.</li>
                <li><span className="font-medium">The right to data portability:</span> You can request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.</li>
                <li><span className="font-medium">The right to withdraw consent:</span> You can withdraw your consent to our email list at any time by clicking the "unsubscribe" link in any email.</li>
              </ul>
              <p className="text-slate-700 mt-4">To exercise any of these rights, please contact us at hello@threadit.ai.</p>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">8. Data Security</h2>
              <p className="text-slate-700 leading-relaxed">
                We take the security of your data seriously and use appropriate technical and organizational measures to protect it from unauthorized access, loss, or misuse.
              </p>
            </section>

            {/* Cookie Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">9. Cookie Policy</h2>
              <p className="text-slate-700 mb-4">Cookies are small files stored on your device that help our website function. We use cookies for two main purposes:</p>
              <ul className="text-slate-700 space-y-2 ml-4">
                <li><span className="font-medium">Essential Cookies:</span> These are necessary for the website to function correctly. For example, we use a cookie to remember your consent choices so you don't see the cookie banner on every visit.</li>
                <li><span className="font-medium">Analytics Cookies:</span> These cookies (e.g., from Google Analytics) help us understand how visitors interact with our website by collecting information anonymously. These are only used if you provide your explicit consent by accepting them in our cookie banner.</li>
              </ul>
              <p className="text-slate-700 mt-4">You can manage your cookie preferences at any time by clearing your browser's cookies, which will cause our consent banner to reappear on your next visit.</p>
            </section>

            {/* Changes to This Privacy Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">10. Changes to This Privacy Policy</h2>
              <p className="text-slate-700 leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.
              </p>
            </section>

            {/* Contact Us */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">11. Contact Us</h2>
              <p className="text-slate-700 mb-2">If you have any questions about this privacy policy or our data practices, please contact us at:</p>
              <p className="text-slate-700 font-medium">hello@threadit.ai</p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 border-t border-slate-200 mt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-8 mb-4 md:mb-0">
              <div className="flex items-center space-x-2 text-xl font-bold text-slate-800">
                <svg className="w-7 h-7 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span>ThreadIt</span>
              </div>
              <div className="flex space-x-6 text-sm text-slate-500">
                <a href="/privacy" className="hover:text-emerald-500 transition-colors">Privacy Policy</a>
                <a href="/terms" className="hover:text-emerald-500 transition-colors">Terms of Service</a>
                <a href="/impressum" className="hover:text-emerald-500 transition-colors">Impressum</a>
              </div>
            </div>
            <div className="flex space-x-6 text-slate-500">
              <a href="/#features" className="hover:text-emerald-500 transition-colors">Features</a>
              <a href="/pricing" className="hover:text-emerald-500 transition-colors">Pricing</a>
              <a href="/contact" className="hover:text-emerald-500 transition-colors">Contact</a>
            </div>
          </div>
          <div className="mt-8 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} ThreadIt Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
